import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardBody, CardExpandToggler } from './../../components/card/card.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';
import useConfirmDialog from '../../hooks/useConfirmDialog.js';
import authService from '../../services/authService.js';
import toastService from '../../services/toastService.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './../../components/ui/tabs.jsx';
import rtoService from '../../services/rtoService.js'

function Agents() {
	// API endpoints
	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
	const JOBS_BASE = `${API_BASE_URL}/jobs`;
	const AGENTS_ENDPOINT = `${JOBS_BASE}/agents`;
	const START_ENDPOINT = `${JOBS_BASE}/start`;
	const STATUS_ENDPOINT = `${JOBS_BASE}/status`;
	const RESULTS_ENDPOINT = `${JOBS_BASE}/results`;
	const CANCEL_ENDPOINT = `${JOBS_BASE}/cancel`;
	const STREAM_ENDPOINT = `${JOBS_BASE}/stream`;
	const HISTORY_ENDPOINT = `${JOBS_BASE}/history`;
	// New: agent file endpoint
	const AGENT_FILE_ENDPOINT = `${JOBS_BASE}/agents/file`;

	// State variables
	const [agents, setAgents] = useState([]);
	const [selectedAgent, setSelectedAgent] = useState(null);
	const [target, setTarget] = useState('');
	const [options, setOptions] = useState('');
	const [jobId, setJobId] = useState(null);
	const [jobStatus, setJobStatus] = useState(null);
	const [jobResult, setJobResult] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [logs, setLogs] = useState([]);
	const [isStreaming, setIsStreaming] = useState(false);
	const streamControllerRef = useRef(null);
	const logsContainerRef = useRef(null);
	const [history, setHistory] = useState([]);
	const [expandedJob, setExpandedJob] = useState(null);
	const { dialogState, showConfirm } = useConfirmDialog();

	// Filters & pagination state (restored)
	const [searchQuery, setSearchQuery] = useState('');
	const [filterAgent, setFilterAgent] = useState('');
	const [filterStatus, setFilterStatus] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(15);

	// Agents Manager editor state (restored)
	const [showAgentEditor, setShowAgentEditor] = useState(false);
	const [editorContent, setEditorContent] = useState('');
	const [editorFilename, setEditorFilename] = useState('');
	const [editorIsEditing, setEditorIsEditing] = useState(false);
	const [editorError, setEditorError] = useState('');

	// Anonymizer (Tor) controls
	const [useTor, setUseTor] = useState(false);
	const [torStatus, setTorStatus] = useState(null);
	const [torExit, setTorExit] = useState(null);
	const [torLoading, setTorLoading] = useState(false);
	const [torError, setTorError] = useState(null);
	// Derives lists for filters
	const uniqueAgents = useMemo(() => {
		return Array.from(new Set(history.map(h => h.agent))).filter(Boolean).sort();
	}, [history]);
	const uniqueStatuses = useMemo(() => {
		return Array.from(new Set(history.map(h => h.status))).filter(Boolean);
	}, [history]);
	// Derived custom agents list
	const customAgents = useMemo(() => agents.filter(a => a.type === 'custom'), [agents]);

	// Filtered, sorted history
	const filteredHistory = useMemo(() => {
		let arr = history;
		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase();
			arr = arr.filter(e => String(e.job_id).toLowerCase().includes(q)
				|| (e.agent && e.agent.toLowerCase().includes(q))
				|| (e.target && e.target.toLowerCase().includes(q)));
		}
		if (filterAgent) arr = arr.filter(e => e.agent === filterAgent);
		if (filterStatus) arr = arr.filter(e => e.status === filterStatus);
		// Sort by created desc, fallback job_id desc
		arr = [...arr].sort((a, b) => {
			const ad = a.created ? new Date(a.created).getTime() : 0;
			const bd = b.created ? new Date(b.created).getTime() : 0;
			if (ad !== bd) return bd - ad;
			const aj = Number(a.job_id) || 0;
			const bj = Number(b.job_id) || 0;
			return bj - aj;
		});
		return arr;
	}, [history, searchQuery, filterAgent, filterStatus]);

	// Reset page on filters/search change
	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterAgent, filterStatus, pageSize]);

	// Pagination slicing
	const totalItems = filteredHistory.length;
	const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
	const currentPage = Math.min(page, totalPages);
	const startIdx = (currentPage - 1) * pageSize;
	const endIdx = startIdx + pageSize;
	const pageItems = filteredHistory.slice(startIdx, endIdx);

	// In-memory history entry updater (component scope)
	const updateHistoryEntry = (job_id, patch) => {
		setHistory(prev => prev.map(e => (e.job_id === job_id ? { ...e, ...patch } : e)));
	};

	// Fetch history from backend
	const fetchHistory = async () => {
		try {
			const res = await fetch(HISTORY_ENDPOINT, { headers: { ...authService.getAuthHeaders() } });
			if (!res.ok) throw new Error(`Failed to fetch history: ${res.status}`);
			const data = await res.json();
			const raw = Array.isArray(data.jobs) ? data.jobs : [];
			// Client-side scoping: admin -> all; superuser -> same org; user -> own
			const me = authService.getCurrentUser();
			const role = me?.role;
			const myId = me?.id != null ? String(me.id) : null;
			const myOrg = me?.organisation ?? me?.organization ?? me?.org ?? me?.orgId ?? me?.org_id ?? null;
			const scoped = raw.filter((e) => {
				if (role === 'admin') return true;
				const owner = e.user_id ?? e.owner_id ?? e.created_by ?? e.user ?? e.owner;
				const org = e.organisation ?? e.organization ?? e.org ?? e.orgId ?? e.org_id;
				if (role === 'superuser') {
					if (org != null && myOrg != null) return String(org) === String(myOrg);
					if (owner != null && myId != null) return String(owner) === String(myId);
					return false;
				}
				// user
				if (owner != null && myId != null) return String(owner) === String(myId);
				return false;
			});
			setHistory(scoped);
		} catch (e) {
			console.error('Error fetching history:', e);
		}
	};

	// New: fetch agents list helper
	const fetchAgents = async () => {
		try {
			const response = await fetch(AGENTS_ENDPOINT, { headers: { ...authService.getAuthHeaders() } });
			if (!response.ok) throw new Error(`Failed to fetch agents: ${response.status}`);
			const data = await response.json();
			setAgents(data.agents || []);
		} catch (err) {
			console.error('Error fetching agents:', err);
			toastService.error('Failed to fetch agents');
			return false;
		}
	};

	// Initial data load
	useEffect(() => { fetchAgents(); }, [AGENTS_ENDPOINT]);
	useEffect(() => { fetchHistory(); }, [HISTORY_ENDPOINT]);

	// Tor status refresh
	useEffect(() => {
		const refresh = async () => {
			try {
				const s = await rtoService.torStatus();
				setTorStatus(s);
				setTorError(null);
			} catch (e) {
				setTorStatus(null);
				setTorError(e?.message || 'Failed to fetch Tor status');
			}
		};
		refresh();
		const id = setInterval(refresh, 30000);
		return () => clearInterval(id);
	}, []);

	const refreshTorStatus = async () => {
		try {
			const s = await rtoService.torStatus();
			setTorStatus(s);
			setTorError(null);
		} catch (e) {
			setTorStatus(null);
			setTorError(e?.message || 'Failed to fetch Tor status');
		}
	};

	const testViaTor = async () => {
		setTorLoading(true);
		setTorError(null);
		setTorExit(null);
		try {
			const res = await rtoService.torExitIP();
			setTorExit(res);
		} catch (e) {
			setTorError(e?.message || 'Failed to fetch Tor exit IP');
		} finally {
			setTorLoading(false);
		}
	};

	const torBadge = () => {
		if (!torStatus) return <span className="badge bg-light text-dark">Tor: unknown</span>;
		const avail = !!torStatus.available;
		const torsocks = !!torStatus.torsocks_present;
		const ver = torStatus.tor_version || '';
		const conn = torStatus.control_connected === true;
		const cls = avail ? (conn ? 'bg-success' : 'bg-warning text-dark') : 'bg-danger';
		const title = avail ? (conn ? 'Tor connected' : 'Tor available, not connected') : 'Tor not available';
		return <span className={`badge ${cls}`} title={title}>{ver?` ${ver}:`:''}{torsocks?' ts':''}</span>;
	};

	const torStatusIcons = () => {
		const s = torStatus || {};
		const Icon = ({ ok, on, off, title }) => (
			<i className={`bi ${ok ? on : off} ${ok ? 'text-success' : 'text-muted'}`} title={title}></i>
		);
		return (
			<span className="d-inline-flex align-items-center gap-2">
				<Icon ok={!!s.torsocks_present} on="bi-shield-lock-fill" off="bi-shield" title="torsocks installed" />
				<Icon ok={!!s.control_connected} on="bi-plug-fill" off="bi-plug" title="Tor control port reachable" />
				<Icon ok={!!s.available} on="bi-wifi" off="bi-wifi-off" title={s.note || 'Tor availability'} />
			</span>
		);
	};

	// New: Agents Manager helpers
	const openCreateAgent = () => {
		setEditorFilename('');
		setEditorContent('# New CPAI agent\nname: "My Agent"\ndescription: ""\nsteps: []\n');
		setEditorIsEditing(false);
		setEditorError('');
		setShowAgentEditor(true);
	};
	const openEditAgent = async (file) => {
		try {
			const res = await fetch(`${AGENT_FILE_ENDPOINT}/${encodeURIComponent(file)}`, { headers: { ...authService.getAuthHeaders() } });
			if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
			const data = await res.json();
			setEditorFilename(data.filename || file);
			setEditorContent(data.content || '');
			setEditorIsEditing(true);
			setEditorError('');
			setShowAgentEditor(true);
		} catch (e) {
			console.error('Load agent file error:', e);
			setEditorError('Failed to load agent file.');
			toastService.error('Failed to load agent file');
		}
	};
	const saveAgent = async () => {
		setEditorError('');
		try {
			const url = editorIsEditing
				? `${AGENTS_ENDPOINT}/${encodeURIComponent(editorFilename)}`
				: `${AGENTS_ENDPOINT}`;
			const method = editorIsEditing ? 'PUT' : 'POST';
			const res = await fetch(url, {
				method,
				headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
				body: JSON.stringify({ filename: editorFilename || undefined, content: editorContent })
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.detail || `Save failed: ${res.status}`);
			}
			setShowAgentEditor(false);
			// refresh agents list and selection
			await fetchAgents();
			toastService.success(editorIsEditing ? 'Agent updated' : 'Agent created');
		} catch (e) {
			console.error('Save agent error:', e);
			setEditorError(e.message || 'Failed to save agent.');
			toastService.error(e.message || 'Failed to save agent');
		}
	};
	const removeAgent = async (file) => {
		const confirmed = await showConfirm({
			title: 'Delete agent',
			message: `Delete custom agent file "${file}"?`,
			confirmText: 'Delete',
			confirmVariant: 'danger'
		});
		if (!confirmed) return;
		try {
			const res = await fetch(`${AGENTS_ENDPOINT}/${encodeURIComponent(file)}`, { method: 'DELETE', headers: { ...authService.getAuthHeaders() } });
			if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
			await fetchAgents();
			toastService.success('Agent deleted');
		} catch (e) {
			console.error('Delete agent error:', e);
			setEditorError('Failed to delete agent.');
			toastService.error('Failed to delete agent');
		}
	};

	// Poll job status when jobId is available
	useEffect(() => {
		let statusInterval;

		const checkJobStatus = async () => {
			if (!jobId) return;

			try {
				const response = await fetch(`${STATUS_ENDPOINT}/${jobId}`, { headers: { ...authService.getAuthHeaders() } });
				if (!response.ok) {
					throw new Error(`Failed to fetch job status: ${response.status}`);
				}
				const data = await response.json();
				setJobStatus(data);
				// history updater is defined at component scope
				// If job is completed or failed or cancelled, stop polling and fetch results
				if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
					clearInterval(statusInterval);
					if (data.status === 'completed') {
						fetchJobResults(jobId);
					}
				}
			} catch (err) {
				console.error('Error checking job status:', err);
				setError('Failed to check job status. Please try again later.');
				clearInterval(statusInterval);
			}
		};

		if (jobId) {
			// Initial check
			checkJobStatus();
			// Set up polling every 3 seconds
			statusInterval = setInterval(checkJobStatus, 3000);
		}

		return () => {
			if (statusInterval) clearInterval(statusInterval);
		};
	}, [jobId, STATUS_ENDPOINT]);

	// Auto scroll logs to bottom
	useEffect(() => {
		if (logsContainerRef.current) {
			logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
		}
	}, [logs]);

	// Start a new job
	const handleStartJob = async (e) => {
		e.preventDefault();

		if (!selectedAgent || !target) {
			setError('Please select an agent and provide a target.');
			return;
		}

		try {
			setLoading(true);
			setError(null);
			setJobId(null);
			setJobStatus(null);
			setJobResult(null);
			setLogs([]);

			let parsedOptions = {};
			if (options) {
				try {
					parsedOptions = JSON.parse(options);
				} catch (err) {
					setError('Invalid JSON in options field. Please check the format.');
					setLoading(false);
					return;
				}
			}

			// Inject anonymizer option from UI toggle
			if (useTor) {
				parsedOptions = { ...parsedOptions, anonymizer: 'tor' };
			}

			const payload = {
				agent_name: selectedAgent.name, // Changed from selectedAgent.path to selectedAgent.name
				path: selectedAgent.path || selectedAgent.file || selectedAgent.filepath || selectedAgent.name,
				target: target,
				options: parsedOptions
			};

			const response = await fetch(START_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...authService.getAuthHeaders()
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				throw new Error(`Failed to start job: ${response.status}`);
			}

			const data = await response.json();
			setJobId(data.job_id);
			setJobStatus({ status: data.status, created: new Date().toISOString() });
			toastService.success(`Job started #${data.job_id}`);
			// Refresh history from backend after starting
			fetchHistory();
		} catch (err) {
			console.error('Error starting job:', err);
			setError('Failed to start job. Please try again later.');
			toastService.error('Failed to start job');
		} finally {
			setLoading(false);
		}
	};

	// Fetch job results
	const fetchJobResults = async (id) => {
		try {
			const response = await fetch(`${RESULTS_ENDPOINT}/${id}`, { headers: { ...authService.getAuthHeaders() } });
			if (!response.ok) {
				throw new Error(`Failed to fetch job results: ${response.status}`);
			}
			const data = await response.json();
			setJobResult(data);
			updateHistoryEntry(id, { result: data.result });
		} catch (err) {
			console.error('Error fetching job results:', err);
			setError('Failed to load job results. Please try again later.');
			toastService.error('Failed to load job results');
		}
	};

	// SSE stream for logs via fetch with Authorization header
	const startLogStream = async (id) => {
		try {
			setLogs([]);
			if (streamControllerRef.current) {
				streamControllerRef.current.abort();
			}
			const controller = new AbortController();
			streamControllerRef.current = controller;
			setIsStreaming(true);
			const res = await fetch(`${STREAM_ENDPOINT}/${id}`, {
				headers: { ...authService.getAuthHeaders() },
				signal: controller.signal
			});
			if (!res.ok) {
				throw new Error(`Failed to connect to logs stream: ${res.status}`);
			}
			const reader = res.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';
			while (true) {
				const { value, done } = await reader.read();
				if (done) break;
				buffer += decoder.decode(value, { stream: true });
				let idx;
				while ((idx = buffer.indexOf('\n\n')) >= 0) {
					const chunk = buffer.slice(0, idx);
					buffer = buffer.slice(idx + 2);
					const lines = chunk.split('\n');
					let event = 'message';
					let dataLines = [];
					for (const line of lines) {
						if (line.startsWith('event:')) {
							event = line.slice(6).trim();
						} else if (line.startsWith('data:')) {
							dataLines.push(line.slice(5).trim());
						}
					}
					const data = dataLines.join('\n');
					if (event === 'log') {
						setLogs(prev => [...prev, data]);
					} else if (event === 'done') {
						setIsStreaming(false);
						// Do not abort the controller here to avoid AbortError noise
						return;
					}
				}
			}
		} catch (e) {
			// Treat AbortError as a normal shutdown (unmount or manual stop)
			if ((e && e.name === 'AbortError') || (streamControllerRef.current && streamControllerRef.current.signal.aborted)) {
				// aborted intentionally; no-op
			} else {
				console.error('Streaming error:', e);
				setIsStreaming(false);
				toastService.warning('Log stream interrupted');
			}
		}
	};

	// Start log stream when jobId changes
	useEffect(() => {
		if (jobId) {
			startLogStream(jobId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jobId]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (streamControllerRef.current) {
				streamControllerRef.current.abort();
			}
		};
	}, []);

	const handleCancelJob = async () => {
		if (!jobId) return;
		const confirmed = await showConfirm({
			title: 'Cancel Job',
			message: `Are you sure you want to cancel job ${jobId}?`,
			confirmText: 'Cancel Job',
			confirmVariant: 'danger'
		});
		if (!confirmed) return;
		try {
			const res = await fetch(`${CANCEL_ENDPOINT}/${jobId}`, {
				method: 'POST',
				headers: { ...authService.getAuthHeaders() }
			});
			if (!res.ok) throw new Error(`Failed to cancel: ${res.status}`);
			const data = await res.json();
			setJobStatus(prev => ({ ...(prev || {}), status: data.status }));
			updateHistoryEntry(jobId, { status: data.status });
			toastService.info('Job cancelled');
		} catch (e) {
			console.error('Cancel job error:', e);
			setError('Failed to cancel the job.');
			toastService.error('Failed to cancel the job');
		}
	};

	// New: handlers for UI controls
	const handleRefreshHistory = async () => {
		const ok = await fetchHistory();
		if (ok) {
			toastService.success('History refreshed');
		}
	};
	const handleResetFilters = () => {
		setSearchQuery('');
		setFilterAgent('');
		setFilterStatus('');
		toastService.info('Filters reset');
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	// Format JSON for display
	const formatJSON = (jsonData) => {
		if (!jsonData) return '';
		let result = jsonData.result;
		
		// If result is a string that looks like JSON, parse it
		if (typeof result === 'string') {
			try {
				result = JSON.parse(result);
			} catch (e) {
				// Not valid JSON, leave as string
			}
		}

		return (
			<pre className="bg-light p-3 rounded">
				{typeof result === 'object' ? JSON.stringify(result, null, 2) : result}
			</pre>
		);
	};

	return (
		<div>
			<ul className="breadcrumb">
				<li className="breadcrumb-item"><a href="#/">Admin</a></li>
				<li className="breadcrumb-item active">Agents</li>
			</ul>
		
			<h1 className="page-header">
				Agents <small>manage CPAI agents directly</small>
			</h1>

			{/* Tabs for Run and Manage */}
			<Tabs defaultValue="run">
				<TabsList className="nav-tabs-cp mb-3">
					<TabsTrigger value="run"><i className="bi bi-play-circle"></i> <span>Run</span></TabsTrigger>
					<TabsTrigger value="manage"><i className="bi bi-gear"></i> <span>Manage</span></TabsTrigger>
				</TabsList>

				<TabsContent value="run">
					<div className="row">
						{/* Agent Selection and Job Configuration */}
						<div className="col-xl-6">
							<Card className="mb-3">
								<CardBody>
									<div className="d-flex fw-bold small mb-3">
										<span className="flex-grow-1">AGENT CONFIGURATION</span>
										<CardExpandToggler />
									</div>
									
									{error && (
										<div className="alert alert-danger">{error}</div>
									)}

									{/* Anonymizer panel */}
									<div className="d-flex align-items-center flex-wrap gap-2 mb-3">
										<span className="fw-bold small">Anonymizer</span>
										{torBadge()}
										{torStatusIcons()}
										<div className="form-check form-switch ms-2">
											<input className="form-check-input" type="checkbox" id="useTorSwitch" checked={useTor} onChange={(e)=>setUseTor(e.target.checked)} />
											<label className="form-check-label" htmlFor="useTorSwitch">Run via Tor</label>
										</div>
										<button className="btn btn-sm btn-warning" onClick={testViaTor} disabled={torLoading} title="Check Tor exit IP">
											{torLoading ? (
												<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Testing…</>
											) : (
												<><i className="bi bi-shield-lock me-1"></i>Test via Tor</>
											)}
										</button>
										{torExit?.ip && (
											<span className="badge bg-dark" title={`Via: ${torExit.via || 'tor'}`}>Exit IP: {torExit.ip}</span>
										)}
										<button className="btn btn-sm btn-outline-secondary" onClick={refreshTorStatus} title="Refresh Tor status">
											<i className="bi bi-arrow-clockwise"></i>
										</button>
										{torError && (
											<div className="alert alert-warning py-1 small mb-0 w-100">{torError}</div>
										)}
									</div>
									
									<form onSubmit={handleStartJob}>
										<div className="mb-3">
											<label htmlFor="agentSelect" className="form-label">Select Agent</label>
											<select 
												id="agentSelect" 
												className="form-select" 
												value={selectedAgent ? selectedAgent.name : ''}
												onChange={(e) => {
													const agentName = e.target.value;
													const agent = agents.find(a => a.name === agentName);
													setSelectedAgent(agent || null);
												}}
												disabled={loading}
											>
												<option value="">-- Select an agent --</option>
												{agents.map((agent) => (
													<option key={agent.name} value={agent.name}>
														{agent.name} - {agent.description}
													</option>
												))}
											</select>
										</div>
										
										<div className="mb-3">
											<label htmlFor="targetInput" className="form-label">Target</label>
											<input 
												type="text" 
												id="targetInput" 
												className="form-control" 
												value={target}
												onChange={(e) => setTarget(e.target.value)}
												placeholder="Enter target (e.g., URL, IP address)"
												disabled={loading}
											/>
										</div>
										
										<div className="mb-3">
											<label htmlFor="optionsInput" className="form-label">Options (JSON format)</label>
											<textarea 
												id="optionsInput" 
												className="form-control" 
												rows="4"
												value={options}
												onChange={(e) => setOptions(e.target.value)}
												placeholder='{"option1": "value1", "option2": true}'
												disabled={loading}
											></textarea>
											<div className="form-text">Optional: Provide additional parameters in JSON format</div>
										</div>
										
										<button 
											type="submit" 
											className="btn btn-theme"
											disabled={loading || !selectedAgent || !target}
										>
											{loading ? 'Starting...' : 'Start Job'}
										</button>
									</form>
								</CardBody>
							</Card>
						</div>

						{/* Job Status and Results */}
						<div className="col-xl-6">
							{jobId && (
								<Card className="mb-3">
									<CardBody>
										<div className="d-flex fw-bold small mb-3">
											<span className="flex-grow-1">JOB STATUS</span>
											<CardExpandToggler />
										</div>

										<div className="mb-3">
											<div className="d-flex align-items-center mb-2">
												<div className="fw-bold me-2">Job ID:</div>
												<div className="text-theme">{jobId}</div>
											</div>

											<div className="d-flex align-items-center mb-2">
												<div className="fw-bold me-2">Status:</div>
												<div>
													{jobStatus ? (
														<span className={`badge ${jobStatus.status === 'completed' ? 'bg-success' : jobStatus.status === 'failed' ? 'bg-danger' : jobStatus.status === 'cancelled' ? 'bg-secondary' : 'bg-warning'}`}>
														{jobStatus.status}
														</span>
													) : (
														<span className="badge bg-secondary">Unknown</span>
													)}
												</div>
												{(jobStatus && (jobStatus.status === 'running' || jobStatus.status === 'pending') && authService.hasRole('admin')) && (
													<button className="btn btn-outline-danger btn-sm ms-3" onClick={handleCancelJob}>
														Cancel Job
													</button>
												)}
											</div>

										{jobStatus && jobStatus.created && (
											<div className="d-flex align-items-center mb-2">
												<div className="fw-bold me-2">Created:</div>
												<div>{formatDate(jobStatus.created)}</div>
											</div>
										)}

										{jobStatus && jobStatus.finished && (
											<div className="d-flex align-items-center mb-2">
												<div className="fw-bold me-2">Finished:</div>
												<div>{formatDate(jobStatus.finished)}</div>
											</div>
										)}
									</div>

									{jobStatus && jobStatus.status === 'running' && (
										<div className="progress mb-3">
											<div 
												className="progress-bar progress-bar-striped progress-bar-animated bg-theme" 
												role="progressbar" 
												style={{ width: '100%' }}
											></div>
										</div>
									)}

									{/* Live Logs */}
									<div className="mb-3">
										<div className="d-flex align-items-center mb-2">
											<div className="fw-bold me-2">Live Logs:</div>
											{isStreaming ? (
												<span className="badge bg-primary">Streaming</span>
											) : (
												(jobStatus && (jobStatus.status === 'completed' || jobStatus.status === 'failed' || jobStatus.status === 'cancelled')) ? (
													<span className="badge bg-secondary">Stream ended</span>
												) : null
											)}
										</div>
										<div ref={logsContainerRef} className="bg-dark text-white rounded p-2" style={{ height: '220px', overflowY: 'auto' }}>
											{logs.length === 0 ? (
												<div className="text-muted">No logs yet...</div>
											) : (
												logs.map((line, idx) => (
													<div key={idx} className="font-monospace small">{line}</div>
												))
											)}
										</div>

										{jobResult && (
																	<div>
																		<h5 className="mt-4 mb-3">Results</h5>
																		{formatJSON(jobResult)}
																	</div>
																)}
															</div>
															</CardBody>
								</Card>
							)}
						</div>
					</div>

					{/* Execution History */}
					<div className="row">
						<div className="col-xl-12">
							<Card className="mb-3">
								<CardBody>
									<div className="d-flex fw-bold small mb-3">
										<span className="flex-grow-1">EXECUTION HISTORY</span>
										<CardExpandToggler />
									</div>

									{/* Controls: search, filters, page size, refresh in one row */}
									<div className="row g-2 align-items-end mb-3">
                                <div className="col-md-5">
                                    <label className="form-label">Search</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search by job id, agent, or target"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Agent</label>
                                    <select className="form-select" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)}>
                                        <option value="">All agents</option>
                                        {uniqueAgents.map(a => (
                                            <option key={a} value={a}>{a}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label">Status</label>
                                    <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                        <option value="">All</option>
                                        {uniqueStatuses.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label">Per page</label>
                                    <select className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                        <option value={15}>15</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                                <div className="col-md-1 d-flex justify-content-end gap-2">
                                    <button className="btn btn-sm btn-outline-primary" onClick={handleRefreshHistory}>Refresh</button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={handleResetFilters}>Reset</button>
                                </div>
                            </div>

									{/* History list */}
									{history.length === 0 ? (
										<div className="text-muted">No jobs yet.</div>
									) : (
										filteredHistory.length === 0 ? (
											<div className="text-muted">No results match your search/filters.</div>
										) : (
											<>
												<div className="list-group">
													{pageItems.map(entry => (
														<div key={entry.job_id} className="list-group-item">
															<div className="d-flex justify-content-between align-items-start">
																<div className="me-3">
																	<div className="fw-bold">Job #{entry.job_id}</div>
																	<div className="small text-muted d-flex flex-wrap gap-2 mt-1">
																		<span className="badge bg-light text-dark border"><i className="bi bi-person-gear me-1"></i>Agent: <span className="fw-semibold ms-1">{entry.agent || '-'}</span></span>
																		<span className="badge bg-light text-dark border"><i className="bi bi-bullseye me-1"></i>Target: <span className="fw-semibold ms-1">{entry.target || '-'}</span></span>
																		<span className={`badge ${entry.status === 'completed' ? 'bg-success' : entry.status === 'failed' ? 'bg-danger' : entry.status === 'cancelled' ? 'bg-secondary' : 'bg-warning'}`}><i className="bi bi-flag me-1"></i>{entry.status || 'unknown'}</span>
																	</div>
																	<div className="small text-muted mt-1">
																		<i className="bi bi-clock-history me-1"></i>Created: {entry.created ? formatDate(entry.created) : '-'}
																		{entry.finished && <span className="ms-3"><i className="bi bi-check2-circle me-1"></i>Finished: {formatDate(entry.finished)}</span>}
																	</div>
																</div>
																<div className="d-flex gap-2">
																	<button className="btn btn-sm btn-outline-primary" onClick={() => { setExpandedJob(expandedJob === entry.job_id ? null : entry.job_id); }}>
																		<i className="bi bi-file-earmark-text me-1"></i>View report
																	</button>
																	<button className="btn btn-sm btn-outline-theme" onClick={() => { setJobId(entry.job_id); setJobStatus(null); setJobResult(null); startLogStream(entry.job_id); }}>
																		<i className="bi bi-terminal me-1"></i>Open logs
																	</button>
																</div>
														</div>
														{expandedJob === entry.job_id && (
															<div className="mt-3">
																{entry.result ? formatJSON({ result: entry.result }) : <div className="text-muted">No report yet.</div>}
															</div>
														)}
													</div>
												))}
											</div>

											{/* Pagination controls */}
											<div className="d-flex justify-content-between align-items-center mt-3">
												<div className="text-muted small">
													Showing {startIdx + 1}-{Math.min(endIdx, totalItems)} of {totalItems}
												</div>
												<div className="btn-group">
													<button className="btn btn-sm btn-outline-secondary" disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
													<button className="btn btn-sm btn-outline-secondary disabled">Page {currentPage} / {totalPages}</button>
													<button className="btn btn-sm btn-outline-secondary" disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
												</div>
											</div>
										</>
										)
									)}
								</CardBody>
							</Card>
						</div>
					</div>
				</TabsContent>

				<TabsContent value="manage">
					{authService.hasRole('admin') ? (
						<Card className="mb-3">
							<CardBody>
								<div className="d-flex fw-bold small mb-3">
									<span className="flex-grow-1">AGENTS MANAGER</span>
									<CardExpandToggler />
								</div>
								<div className="d-flex justify-content-between align-items-center mb-2">
									<div className="small text-muted">Manage custom YAML agents. Changes apply immediately.</div>
									<button className="btn btn-sm btn-outline-primary" onClick={openCreateAgent}>
										New Agent
									</button>
								</div>
								{customAgents.length === 0 ? (
									<div className="text-muted mb-2">No custom agents found.</div>
								) : (
									<div className="list-group mb-2">
										{customAgents.map(a => (
											<div key={a.name + (a.file || '')} className="list-group-item d-flex justify-content-between align-items-center">
												<div>
													<div className="fw-semibold">{a.name}</div>
													<div className="small text-muted">{a.file || 'custom'}</div>
												</div>
												<div className="btn-group">
													<button className="btn btn-sm btn-outline-secondary" onClick={() => openEditAgent(a.file)}>Edit</button>
													<button className="btn btn-sm btn-outline-danger" onClick={() => removeAgent(a.file)}>Delete</button>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Inline editor */}
								{showAgentEditor && (
									<div className="border rounded p-2">
										<div className="row g-2 align-items-end">
											<div className="col-md-4">
												<label className="form-label">Filename</label>
												<input type="text" className="form-control form-control-sm" placeholder="auto from YAML name"
													value={editorFilename}
													onChange={(e)=>setEditorFilename(e.target.value)}
													disabled={editorIsEditing}
												/>
											</div>
											<div className="col-12">
												<label className="form-label">YAML</label>
												<textarea className="form-control font-monospace" rows="12" value={editorContent} onChange={(e)=>setEditorContent(e.target.value)}></textarea>
											</div>
											{editorError && <div className="col-12"><div className="alert alert-danger py-1 my-2">{editorError}</div></div>}
											<div className="col-12 d-flex gap-2">
												<button className="btn btn-sm btn-theme" onClick={saveAgent}>{editorIsEditing ? 'Update' : 'Save'}</button>
												<button className="btn btn-sm btn-outline-secondary" onClick={()=>setShowAgentEditor(false)}>Cancel</button>
											</div>
										</div>
									</div>
								)}
							</CardBody>
						</Card>
					) : (
						<Card className="mb-3">
							<CardBody>
								<div className="alert alert-warning mb-0">Only admins can manage custom agents.</div>
							</CardBody>
						</Card>
					)}
				</TabsContent>
			</Tabs>

			<ConfirmDialog
				isVisible={dialogState.isVisible}
				title={dialogState.title}
				message={dialogState.message}
				confirmText={dialogState.confirmText}
				cancelText={dialogState.cancelText}
				confirmVariant={dialogState.confirmVariant}
				onConfirm={dialogState.onConfirm}
				onCancel={dialogState.onCancel}
				size={dialogState.size}
			/>
		</div>
	);
}

export default Agents;